import { SkillDownloadSurface, SkillOrigin } from '@/analytics/enums/skill.enums'
import type { TrackEventFn } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'

type AttachedSkills = {
  selectedSkillIds?: string[]
  selectedPaxtonSkillNames?: string[]
}

const kSkillEventOptions = { suppressFeatureInheritance: true }

export function trackSkillCopied(track: TrackEventFn, skillOrigin: SkillOrigin, skillId?: string) {
  track(AnalyticsEvent.SkillCopied, { skillOrigin, ...(skillId ? { skillId } : {}) }, kSkillEventOptions)
}

export function trackSkillDownloaded(track: TrackEventFn, skillOrigin: SkillOrigin, skillDownloadSurface: SkillDownloadSurface, skillId: string) {
  track(AnalyticsEvent.SkillDownloaded, { skillOrigin, skillDownloadSurface, skillId }, kSkillEventOptions)
}

export function trackSkillCreated(track: TrackEventFn, skillId: string) {
  track(AnalyticsEvent.SkillCreated, { skillId }, kSkillEventOptions)
}

export function trackSkillEdited(track: TrackEventFn, skillId: string) {
  track(AnalyticsEvent.SkillEdited, { skillId }, kSkillEventOptions)
}

export function trackSkillDeleted(track: TrackEventFn, skillId: string) {
  track(AnalyticsEvent.SkillDeleted, { skillId }, kSkillEventOptions)
}

function messageNamesSkill(message: string, skillName: string): boolean {
  const name = skillName.trim()
  if (!name) return false
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp(`(?:^|\\W)${escaped}(?:$|\\W)`, 'i').test(message)
}

function uniqueIds(ids: readonly string[]): string[] {
  const seen = new Set<string>()
  const unique: string[] = []
  for (const id of ids) {
    if (seen.has(id)) continue
    seen.add(id)
    unique.push(id)
  }
  return unique
}

// Paxton names come from the loaded library. A missing name keeps the stored id.
// Counts are the skills attached to this send. A name in the message is only userSkillInvoked.
// The skill id is not on this event. The user-skill name stays off the payload.
export function newChatMessageSkillProperties(
  attached: AttachedSkills | undefined,
  paxtonSkills: readonly { id: string; name: string }[],
  userSkills: readonly { id: string; name: string }[],
  inputText: string
) {
  const skillIds = uniqueIds(attached?.selectedSkillIds ?? [])
  const paxtonSkillIds = attached?.selectedPaxtonSkillNames ?? []
  const nameById = new Map(paxtonSkills.map((skill) => [skill.id, skill.name]))
  return {
    userSkillCount: skillIds.length,
    paxtonSkillCount: paxtonSkillIds.length,
    paxtonSkillsUsed: paxtonSkillIds.map((id) => nameById.get(id) ?? id).join(', '),
    userSkillInvoked: userSkills.some((skill) => messageNamesSkill(inputText, skill.name)),
  }
}
